import { useState, useMemo } from 'react';
import {
  Title,
  Text,
  Stack,
  Paper,
  Table,
  Button,
  Group,
  Modal,
  TextInput,
  Textarea,
  Select,
  Badge,
  Skeleton,
  Alert,
  Card,
  SimpleGrid,
  Tabs,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPackageExport,
  IconPackageImport,
  IconAlertTriangle,
  IconClock,
  IconCheck,
  IconSearch,
  IconCalendar,
} from '@tabler/icons-react';

import {
  useActiveLoans,
  useOverdueLoans,
  useLoanSummary,
  useLoans,
  useBorrowers,
  useAvailableAssets,
  useCheckoutAsset,
  useReturnAsset,
} from '../hooks/useLoans';
import { Loan, LoanStatus } from '../types';

const STATUS_COLORS: Record<LoanStatus, string> = {
  active: 'blue',
  returned: 'green',
  overdue: 'red',
};

export function LoansPage() {
  // Tab state
  const [activeTab, setActiveTab] = useState<string | null>('active');

  // Filters
  const [searchFilter, setSearchFilter] = useState('');

  // Modal state
  const [checkoutOpened, { open: openCheckout, close: closeCheckout }] = useDisclosure(false);
  const [returnOpened, { open: openReturn, close: closeReturn }] = useDisclosure(false);
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);

  // Checkout form
  const [checkoutForm, setCheckoutForm] = useState<{
    assetId: string;
    borrowerId: string;
    dueDate: Date | null;
    notes: string;
  }>({
    assetId: '',
    borrowerId: '',
    dueDate: null,
    notes: '',
  });

  // Return form
  const [returnNotes, setReturnNotes] = useState('');

  // Queries
  const { data: summary } = useLoanSummary();
  const { data: activeLoans, isLoading: activeLoading } = useActiveLoans();
  const { data: overdueData } = useOverdueLoans();
  const { data: allLoans, isLoading: allLoading } = useLoans(
    searchFilter ? { search: searchFilter } : undefined
  );
  const { data: borrowers } = useBorrowers({ isActive: true });
  const { data: availableAssets } = useAvailableAssets();

  // Mutations
  const checkoutMutation = useCheckoutAsset();
  const returnMutation = useReturnAsset();

  // Derived data
  const overdueLoans = overdueData?.loans || [];

  const displayedLoans = useMemo(() => {
    switch (activeTab) {
      case 'active':
        return activeLoans || [];
      case 'overdue':
        return overdueLoans;
      case 'all':
        return allLoans || [];
      default:
        return [];
    }
  }, [activeTab, activeLoans, overdueLoans, allLoans]);

  const isLoading = activeTab === 'active' ? activeLoading : activeTab === 'all' ? allLoading : false;

  // Select options
  const borrowerOptions = useMemo(() => {
    return (
      borrowers?.map((b) => ({
        value: b.id,
        label: `${b.fullName} (${b.identificationNumber})`,
      })) || []
    );
  }, [borrowers]);

  const assetOptions = useMemo(() => {
    return (
      availableAssets?.map((a) => ({
        value: a.id,
        label: `${a.inventoryNumber} - ${a.name}`,
      })) || []
    );
  }, [availableAssets]);

  // Handlers
  const handleOpenCheckout = () => {
    setCheckoutForm({
      assetId: '',
      borrowerId: '',
      dueDate: null,
      notes: '',
    });
    openCheckout();
  };

  const handleOpenReturn = (loan: Loan) => {
    setSelectedLoan(loan);
    setReturnNotes('');
    openReturn();
  };

  const handleCheckout = async () => {
    if (!checkoutForm.assetId || !checkoutForm.borrowerId || !checkoutForm.dueDate) {
      notifications.show({
        title: 'Napaka',
        message: 'Izpolnite vsa obvezna polja',
        color: 'red',
      });
      return;
    }

    try {
      const dueDateStr = checkoutForm.dueDate!.toISOString().slice(0, 10);
      const result = await checkoutMutation.mutateAsync({
        assetId: checkoutForm.assetId,
        borrowerId: checkoutForm.borrowerId,
        dueDate: dueDateStr,
        notes: checkoutForm.notes || undefined,
      });
      notifications.show({
        title: 'Uspeh',
        message: result.message,
        color: 'green',
      });
      closeCheckout();
    } catch (error) {
      notifications.show({
        title: 'Napaka',
        message: error instanceof Error ? error.message : 'Napaka pri izposoji',
        color: 'red',
      });
    }
  };

  const handleReturn = async () => {
    if (!selectedLoan) return;

    try {
      const result = await returnMutation.mutateAsync({
        loanId: selectedLoan.id,
        notes: returnNotes || undefined,
      });
      notifications.show({
        title: 'Uspeh',
        message: result.message,
        color: 'green',
      });
      closeReturn();
      setSelectedLoan(null);
    } catch (error) {
      notifications.show({
        title: 'Napaka',
        message: error instanceof Error ? error.message : 'Napaka pri vračilu',
        color: 'red',
      });
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('sl-SI');
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Title order={2}>Izposoje opreme</Title>
        <Button leftSection={<IconPackageExport size={16} />} onClick={handleOpenCheckout}>
          Nova izposoja
        </Button>
      </Group>

      {/* Summary Cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Aktivne izposoje
              </Text>
              <Text size="xl" fw={700}>
                {summary?.active || 0}
              </Text>
            </div>
            <IconClock size={32} color="var(--mantine-color-blue-5)" />
          </Group>
        </Card>

        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Zamujene
              </Text>
              <Text size="xl" fw={700} c={summary?.overdueCount ? 'red' : undefined}>
                {summary?.overdueCount || 0}
              </Text>
            </div>
            <IconAlertTriangle
              size={32}
              color={
                summary?.overdueCount
                  ? 'var(--mantine-color-red-5)'
                  : 'var(--mantine-color-gray-5)'
              }
            />
          </Group>
        </Card>

        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Vrnjene
              </Text>
              <Text size="xl" fw={700}>
                {summary?.returned || 0}
              </Text>
            </div>
            <IconCheck size={32} color="var(--mantine-color-green-5)" />
          </Group>
        </Card>

        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Skupaj
              </Text>
              <Text size="xl" fw={700}>
                {summary?.total || 0}
              </Text>
            </div>
            <IconPackageExport size={32} color="var(--mantine-color-gray-5)" />
          </Group>
        </Card>
      </SimpleGrid>

      {/* Overdue Alert */}
      {summary?.overdueCount && summary.overdueCount > 0 && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Zamujene izposoje">
          {summary.overdueCount} {summary.overdueCount === 1 ? 'izposoja je zamujenna' : 'izposoj je zamujenih'}!
          Preverite zavihek "Zamujene" za podrobnosti.
        </Alert>
      )}

      {/* Tabs */}
      <Paper withBorder>
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="active" leftSection={<IconClock size={14} />}>
              Aktivne ({summary?.active || 0})
            </Tabs.Tab>
            <Tabs.Tab
              value="overdue"
              leftSection={<IconAlertTriangle size={14} />}
              color={summary?.overdueCount ? 'red' : undefined}
            >
              Zamujene ({summary?.overdueCount || 0})
            </Tabs.Tab>
            <Tabs.Tab value="all" leftSection={<IconPackageExport size={14} />}>
              Vse ({summary?.total || 0})
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="active" p="md">
            <LoanTable
              loans={displayedLoans}
              isLoading={isLoading}
              onReturn={handleOpenReturn}
              showStatus={false}
            />
          </Tabs.Panel>

          <Tabs.Panel value="overdue" p="md">
            <LoanTable
              loans={displayedLoans}
              isLoading={isLoading}
              onReturn={handleOpenReturn}
              showStatus={false}
              highlightOverdue
            />
          </Tabs.Panel>

          <Tabs.Panel value="all" p="md">
            <Stack>
              <TextInput
                placeholder="Iskanje..."
                leftSection={<IconSearch size={16} />}
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                style={{ maxWidth: 300 }}
              />
              <LoanTable
                loans={displayedLoans}
                isLoading={isLoading}
                onReturn={handleOpenReturn}
                showStatus
              />
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Paper>

      {/* Checkout Modal */}
      <Modal opened={checkoutOpened} onClose={closeCheckout} title="Nova izposoja" size="lg">
        <Stack>
          <TextInput
            label="Inventarna stevilka"
            placeholder="Vnesite inv. st. za hitro iskanje"
            value={
              checkoutForm.assetId
                ? availableAssets?.find((a) => a.id === checkoutForm.assetId)?.inventoryNumber || ''
                : ''
            }
            onChange={(e) => {
              const invNum = e.target.value.trim();
              const matched = availableAssets?.find(
                (a) => a.inventoryNumber.toLowerCase() === invNum.toLowerCase()
              );
              if (matched) {
                setCheckoutForm({ ...checkoutForm, assetId: matched.id });
              } else if (!invNum) {
                setCheckoutForm({ ...checkoutForm, assetId: '' });
              }
            }}
            onBlur={(e) => {
              const invNum = e.target.value.trim();
              const matched = availableAssets?.find(
                (a) => a.inventoryNumber.toLowerCase() === invNum.toLowerCase()
              );
              if (matched) {
                setCheckoutForm({ ...checkoutForm, assetId: matched.id });
              }
            }}
          />

          <Select
            label="Oprema"
            required
            placeholder="Ali izberite iz seznama"
            searchable
            data={assetOptions}
            value={checkoutForm.assetId}
            onChange={(v) => setCheckoutForm({ ...checkoutForm, assetId: v || '' })}
            nothingFoundMessage="Ni razpoloizljive opreme"
          />

          <Select
            label="Izposojevalec"
            required
            placeholder="Izberite izposojevalca"
            searchable
            data={borrowerOptions}
            value={checkoutForm.borrowerId}
            onChange={(v) => setCheckoutForm({ ...checkoutForm, borrowerId: v || '' })}
            nothingFoundMessage="Ni izposojevalcev"
          />

          <DateInput
            label="Rok vračila"
            required
            placeholder="Izberite datum"
            value={checkoutForm.dueDate}
            onChange={(v: Date | null) => setCheckoutForm({ ...checkoutForm, dueDate: v })}
            minDate={new Date()}
            leftSection={<IconCalendar size={16} />}
          />

          <Textarea
            label="Opombe"
            placeholder="Dodatne opombe..."
            minRows={2}
            value={checkoutForm.notes}
            onChange={(e) => setCheckoutForm({ ...checkoutForm, notes: e.target.value })}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeCheckout}>
              Prekliči
            </Button>
            <Button onClick={handleCheckout} loading={checkoutMutation.isPending}>
              Izposodi
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Return Modal */}
      <Modal opened={returnOpened} onClose={closeReturn} title="Vračilo opreme" size="md">
        <Stack>
          {selectedLoan && (
            <>
              <Paper withBorder p="md">
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Oprema:
                    </Text>
                    <Text fw={500}>{selectedLoan.assetName}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Inv. št.:
                    </Text>
                    <Text ff="monospace">{selectedLoan.assetInventoryNumber}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Izposojevalec:
                    </Text>
                    <Text>{selectedLoan.borrowerName}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Izposojeno:
                    </Text>
                    <Text>{formatDate(selectedLoan.loanedAt)}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Rok vračila:
                    </Text>
                    <Text c={selectedLoan.isOverdue ? 'red' : undefined}>
                      {formatDate(selectedLoan.dueDate)}
                    </Text>
                  </Group>
                </Stack>
              </Paper>

              {selectedLoan.isOverdue && (
                <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                  Ta izposoja je zamujenna za {selectedLoan.daysOverdue}{' '}
                  {selectedLoan.daysOverdue === 1 ? 'dan' : 'dni'}!
                </Alert>
              )}

              <Textarea
                label="Opombe ob vračilu"
                placeholder="Morebitne poškodbe, opombe..."
                minRows={2}
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
              />

              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={closeReturn}>
                  Prekliči
                </Button>
                <Button
                  color="green"
                  leftSection={<IconPackageImport size={16} />}
                  onClick={handleReturn}
                  loading={returnMutation.isPending}
                >
                  Potrdi vračilo
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
}

// Sub-component for loan table
interface LoanTableProps {
  loans: Loan[];
  isLoading: boolean;
  onReturn: (loan: Loan) => void;
  showStatus?: boolean;
  highlightOverdue?: boolean;
}

function LoanTable({ loans, isLoading, onReturn, showStatus = false, highlightOverdue = false }: LoanTableProps) {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('sl-SI');
  };

  if (isLoading) {
    return (
      <Stack>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} height={40} />
        ))}
      </Stack>
    );
  }

  if (loans.length === 0) {
    return (
      <Stack align="center" py="xl">
        <IconPackageExport size={48} color="var(--mantine-color-gray-5)" />
        <Text c="dimmed">Ni izposoj</Text>
      </Stack>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Oprema</Table.Th>
          <Table.Th>Inv. št.</Table.Th>
          <Table.Th>Izposojevalec</Table.Th>
          <Table.Th>Izposojeno</Table.Th>
          <Table.Th>Rok vračila</Table.Th>
          {showStatus && <Table.Th>Status</Table.Th>}
          <Table.Th style={{ width: 120 }}>Akcija</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {loans.map((loan) => (
          <Table.Tr
            key={loan.id}
            style={
              highlightOverdue && loan.isOverdue
                ? { backgroundColor: 'var(--mantine-color-red-0)' }
                : undefined
            }
          >
            <Table.Td>
              <Text fw={500}>{loan.assetName}</Text>
            </Table.Td>
            <Table.Td>
              <Text ff="monospace" size="sm">
                {loan.assetInventoryNumber}
              </Text>
            </Table.Td>
            <Table.Td>
              <Stack gap={0}>
                <Text size="sm">{loan.borrowerName}</Text>
                <Text size="xs" c="dimmed">
                  {loan.borrowerIdentification}
                </Text>
              </Stack>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{formatDate(loan.loanedAt)}</Text>
            </Table.Td>
            <Table.Td>
              <Group gap="xs">
                <Text size="sm" c={loan.isOverdue ? 'red' : undefined} fw={loan.isOverdue ? 600 : undefined}>
                  {formatDate(loan.dueDate)}
                </Text>
                {loan.isOverdue && (
                  <Tooltip label={`Zamujeno ${loan.daysOverdue} dni`}>
                    <Badge color="red" size="xs">
                      -{loan.daysOverdue}d
                    </Badge>
                  </Tooltip>
                )}
                {!loan.isOverdue && loan.daysUntilDue !== null && loan.daysUntilDue <= 3 && loan.status !== 'returned' && (
                  <Tooltip label={`Še ${loan.daysUntilDue} dni`}>
                    <Badge color="yellow" size="xs">
                      {loan.daysUntilDue}d
                    </Badge>
                  </Tooltip>
                )}
              </Group>
            </Table.Td>
            {showStatus && (
              <Table.Td>
                <Badge color={STATUS_COLORS[loan.status as LoanStatus]}>{loan.statusLabel}</Badge>
              </Table.Td>
            )}
            <Table.Td>
              {loan.status !== 'returned' ? (
                <Button
                  size="xs"
                  variant="light"
                  color="green"
                  leftSection={<IconPackageImport size={14} />}
                  onClick={() => onReturn(loan)}
                >
                  Vrni
                </Button>
              ) : (
                <Text size="sm" c="dimmed">
                  {loan.returnedAt ? formatDate(loan.returnedAt) : '-'}
                </Text>
              )}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
